import * as WebBrowser from 'expo-web-browser';
import { Platform } from 'react-native';
import { billingAppUrl } from '../../../../../shared/subscription-routing';
import { invokeEdgeOrPresent } from '../../shared/supabase/invokeEdgeOrPresent';
import { supabase } from '../../shared/supabase/supabaseClient';
import { normalizePaidPlanCode, type PaidPlanCode } from '../feature-flags/planIntentService';

WebBrowser.maybeCompleteAuthSession();

export type BillingHandoffCreateResult =
  | { ok: true; url: string }
  | { ok: false; error: string; fallbackUrl: string };

/**
 * Pide un código one-shot al Edge y arma la URL de aterrizaje en la PWA.
 * Si falla, devuelve fallback con `?billing=1` (login manual).
 */
export async function createBillingWebHandoffUrl(
  plan?: PaidPlanCode,
): Promise<BillingHandoffCreateResult> {
  const fallbackUrl = billingAppUrl({ billing: true, plan });

  try {
    const { data, error, presented } = await invokeEdgeOrPresent('billing-web-handoff', {
      body: { action: 'create', plan: plan ?? null },
    }, {
      module: 'billing',
      op: 'billing.handoff_create',
      fallbackMessage: 'No se pudo abrir la web',
    });

    if (error) {
      return {
        ok: false,
        error: presented
          ? 'Algo falló. Reintentá en un momento.'
          : error.message || 'No se pudo abrir la web',
        fallbackUrl,
      };
    }

    const payload = data as { url?: string; error?: string } | null;
    if (!payload?.url) {
      return {
        ok: false,
        error: payload?.error || 'Respuesta inválida del servidor',
        fallbackUrl,
      };
    }

    return { ok: true, url: payload.url };
  } catch (e: unknown) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : String(e),
      fallbackUrl,
    };
  }
}

/** Abre la URL de billing en navegador in-app (Custom Tabs / SFSafariViewController). */
export async function openBillingWebUrl(url: string): Promise<void> {
  if (Platform.OS === 'web') {
    if (typeof window !== 'undefined') {
      window.location.assign(url);
    }
    return;
  }
  await WebBrowser.openBrowserAsync(url, {
    presentationStyle: WebBrowser.WebBrowserPresentationStyle.PAGE_SHEET,
    enableDefaultShareMenuItem: false,
    showTitle: true,
  });
}

export type BillingHandoffConsumeResult =
  | { ok: true; plan: PaidPlanCode | null }
  | { ok: false; error: string };

/**
 * Canjea `billing_handoff` en la PWA y establece sesión Supabase en ese origen.
 */
export async function consumeBillingWebHandoff(code: string): Promise<BillingHandoffConsumeResult> {
  try {
    const { data, error, presented } = await invokeEdgeOrPresent('billing-web-handoff', {
      body: { action: 'consume', code },
    }, {
      module: 'billing',
      op: 'billing.handoff_consume',
      fallbackMessage: 'Handoff inválido',
    });
    if (error) {
      return {
        ok: false,
        error: presented ? 'Algo falló. Reintentá en un momento.' : error.message || 'Handoff inválido',
      };
    }
    const payload = data as {
      token_hash?: string;
      email?: string;
      plan?: string | null;
      error?: string;
    } | null;

    if (!payload?.token_hash) {
      return { ok: false, error: payload?.error || 'Código expirado o inválido' };
    }

    const { error: verifyError } = await supabase.auth.verifyOtp({
      token_hash: payload.token_hash,
      type: 'email',
    });

    if (verifyError) {
      return { ok: false, error: verifyError.message };
    }

    return { ok: true, plan: normalizePaidPlanCode(payload.plan) };
  } catch (e: unknown) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

export function readBillingHandoffFromLocation(): string | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get('billing_handoff');
  } catch {
    return null;
  }
}

export function clearBillingHandoffFromLocation(): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('billing_handoff') && !url.searchParams.has('billing')) return;
    url.searchParams.delete('billing_handoff');
    // Keep plan / mp if present; billing flag can go after nav.
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, '', next);
  } catch {
    /* ignore */
  }
}

export function readBillingFlagFromLocation(): boolean {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('billing') === '1';
  } catch {
    return false;
  }
}
